import React, { useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Divider,
  Typography,
  useTheme,
} from "@mui/material";
import { confirmDialogStyle } from "./ConfirmDialogStyle";

export type DialogButton = {
  label: string;
  variant?: "text" | "outlined" | "contained";
  color?: "primary" | "secondary" | "error" | "inherit";
  onClick: () => void;
  disabled?: boolean;
};

type ConfirmDialogProps = {
  open: boolean;
  title: string;
  message: string;
  buttons: DialogButton[];
};

const ConfirmDialog: React.FC<ConfirmDialogProps> = ({
  open,
  title,
  message,
  buttons,
}) => {
  const theme = useTheme();
  const styles = confirmDialogStyle(theme);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (open && e.key === "Enter") {
        e.preventDefault();
        buttons[buttons.length - 1]?.onClick();
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [open, buttons]);

  return (
    <Dialog
      open={open}
      onClose={() => buttons[buttons.length - 1]?.onClick()}
      sx={{ zIndex: 4000 }}
    >
      <DialogTitle sx={styles.title}>{title}</DialogTitle>
      <Divider />
      <DialogContent>
        <div style={{ whiteSpace: "pre-line" }}>{message}</div>
      </DialogContent>
      <Divider />
      <DialogActions sx={styles.actions}>
        {buttons.map((btn, index) => (
          <Button
            key={index}
            variant={btn.variant ?? "text"}
            color={btn.color ?? "primary"}
            onClick={btn.onClick}
            sx={
              btn.variant === "contained"
                ? styles.confirmButton
                : styles.cancelButton
            }
            disabled={btn.disabled}
          >
            {btn.label}
          </Button>
        ))}
      </DialogActions>
    </Dialog>
  );
};

export default ConfirmDialog;
